import React, { FC, useState } from 'react';
import { ReactComponent as AlertTriangleIcon } from '../../icons/alert-triangle-outline.svg';
import { ReactComponent as ProjectsFolderIcon } from '../../icons/projects-folder.svg';
import OrganisationModal from './OrganisationModal';
import { HandbookUploadField, uploadFieldProblem } from './HandbookUploadField';
import type { HandbookPageFill } from './handbookContract';
import { fillErrorMessage } from './handbookFills';
import { TakenTitle, fillSourceTitle, titleConflict } from './handbookSetup';
import './handbook-setup.scss';

type Step = 'about' | 'pages' | 'result';
const STEPS: Step[] = ['about', 'pages', 'result'];

export interface NewSection {
  title: string;
  description: string;
  /** `slug` is set for an existing blank page being filled */
  pages: { title: string; fill: HandbookPageFill | null; slug?: string }[];
}

export interface ExistingSection {
  title: string;
  blankPages: { slug: string; title: string }[];
}

interface HandbookSectionWizardProps {
  busy: boolean;
  /** Writes each page title from the files; throws when that fails */
  fill: (
    files: File[],
    titles: string[],
  ) => Promise<Record<string, HandbookPageFill | null>>;
  onCreate: (section: NewSection) => void;
  onClose: () => void;
  /** Pages and sections the Handbook already has; new titles must not clash */
  taken: TakenTitle[];
  /** Create pages from a document in this section: skips the name step */
  existing?: ExistingSection;
}

const preview = (markdown: string) =>
  markdown
    .replace(/^#+\s*/gm, '')
    .replace(/^\s*[-+]\s+/gm, '')
    .replace(/[*_`>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Guided "Add section": what it's about, its pages, then the filled result. */
export const HandbookSectionWizard: FC<HandbookSectionWizardProps> = ({
  busy,
  fill,
  onCreate,
  onClose,
  taken,
  existing,
}) => {
  const steps: Step[] = existing ? ['pages', 'result'] : STEPS;
  const [step, setStep] = useState<Step>(steps[0]);
  const [title, setTitle] = useState(existing?.title ?? '');
  const [picked, setPicked] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [pages, setPages] = useState<string[]>(['']);
  const [files, setFiles] = useState<File[]>([]);
  const [fills, setFills] = useState<Record<string, HandbookPageFill | null>>(
    {},
  );
  const [filling, setFilling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickedPages = (existing?.blankPages ?? []).filter((p) =>
    picked.includes(p.slug),
  );
  const titles = [
    ...pickedPages.map((p) => p.title),
    ...pages.map((p) => p.trim()).filter(Boolean),
  ];
  const working = busy || filling;
  const at = steps.indexOf(step);
  const blank = titles.filter((t) => !fills[t]);
  const sectionConflict = title.trim()
    ? titleConflict(title.trim(), [], taken)
    : null;
  const newTitles = pages.map((p) => p.trim());
  const pageConflicts = newTitles.map((t, i) =>
    t
      ? titleConflict(
          t,
          [...pickedPages.map((p) => p.title), ...newTitles.slice(0, i)],
          taken,
        )
      : null,
  );
  const hasConflict = pageConflicts.some(Boolean);

  const fillPages = async () => {
    setError(null);
    if (files.length === 0) {
      setFills({});
      setStep('result');
      return;
    }
    setFilling(true);
    try {
      const results = await fill(files, titles);
      setFills(results);
      setStep('result');
    } catch (err) {
      setError(
        fillErrorMessage(err, 'Could not read those documents. Try again.'),
      );
    } finally {
      setFilling(false);
    }
  };

  const submit = () => {
    if (step === 'about') {
      if (title.trim()) setStep('pages');
      return;
    }
    if (step === 'pages') {
      if (titles.length) fillPages();
      return;
    }
    onCreate({
      title: title.trim(),
      description: description.trim(),
      pages: titles.map((t) => ({
        title: t,
        fill: fills[t] ?? null,
        slug: pickedPages.find((p) => p.title === t)?.slug,
      })),
    });
  };

  const renderAbout = () => (
    <div className='organisation-modal-panel handbook-setup-panel'>
      <div className='organisation-modal-field'>
        <label
          className='organisation-modal-label'
          htmlFor='handbook-section-name'
        >
          Section name
        </label>
        <input
          id='handbook-section-name'
          className='organisation-modal-input'
          value={title}
          maxLength={200}
          placeholder='e.g. IT and equipment'
          aria-invalid={!!sectionConflict}
          onChange={(e) => setTitle(e.target.value)}
        />
        {sectionConflict && (
          <p className='organisation-modal-hint handbook-setup-conflict'>
            {sectionConflict}
          </p>
        )}
      </div>
      <div className='organisation-modal-field'>
        <label
          className='organisation-modal-label'
          htmlFor='handbook-section-about'
        >
          What is this section about?
        </label>
        <textarea
          id='handbook-section-about'
          className='organisation-modal-input handbook-setup-textarea short'
          value={description}
          placeholder='e.g. Laptops, accounts and who to ask when something breaks'
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </div>
  );

  const renderBlankPages = () =>
    !!existing?.blankPages.length && (
      <fieldset className='organisation-modal-roles handbook-setup-goals'>
        <legend className='organisation-modal-label'>
          Blank pages to fill
        </legend>
        {existing.blankPages.map((p) => {
          const chosen = picked.includes(p.slug);
          return (
            <label
              key={p.slug}
              className={`organisation-modal-role${chosen ? ' selected' : ''}`}
            >
              <input
                type='checkbox'
                checked={chosen}
                onChange={() =>
                  setPicked(
                    chosen
                      ? picked.filter((s) => s !== p.slug)
                      : [...picked, p.slug],
                  )
                }
              />
              <span
                className='organisation-modal-radio handbook-setup-checkbox'
                aria-hidden='true'
              />
              <strong>{p.title}</strong>
            </label>
          );
        })}
      </fieldset>
    );

  const renderPages = () => (
    <div className='organisation-modal-panel handbook-setup-panel'>
      <div className='organisation-modal-field'>
        <span className='organisation-modal-label'>
          {existing ? 'New pages to add' : 'What page titles will you add?'}
        </span>
        <div className='handbook-setup-section-pages'>
          {pages.map((page, i) => (
            <div className='handbook-setup-section-page' key={i}>
              <input
                aria-label={`Page ${i + 1}`}
                className='organisation-modal-input'
                value={page}
                maxLength={200}
                placeholder='Page title, e.g. Laptops'
                aria-invalid={!!pageConflicts[i]}
                onChange={(e) =>
                  setPages(pages.map((p, k) => (k === i ? e.target.value : p)))
                }
              />
              {pageConflicts[i] && (
                <p className='organisation-modal-hint handbook-setup-conflict'>
                  {pageConflicts[i]}
                </p>
              )}
              {pages.length > 1 && (
                <button
                  type='button'
                  className='handbook-setup-link'
                  onClick={() => setPages(pages.filter((_, k) => k !== i))}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <div className='handbook-setup-section-actions'>
            <button
              type='button'
              className='handbook-setup-link'
              onClick={() => setPages([...pages, ''])}
            >
              Add a page
            </button>
          </div>
        </div>
      </div>
      {renderBlankPages()}
      <HandbookUploadField
        files={files}
        onChange={setFiles}
        label={
          existing
            ? 'Documents to fill your pages from'
            : 'Documents to fill your pages from (optional)'
        }
      />
      <p className='organisation-modal-hint'>
        {existing
          ? 'We’ll fill these pages from your document. To update a page that already has content, open it and pull from a document there.'
          : 'Add a document and we’ll fill these pages from it. Anything in it that doesn’t match a page is left out.'}
      </p>
    </div>
  );

  const renderResult = () => (
    <div className='organisation-modal-panel handbook-setup-panel'>
      <ul className='handbook-section-result' aria-label='Your pages'>
        {titles.map((t) => {
          const hit = fills[t];
          return (
            <li key={t} className='handbook-section-result-page'>
              <div className='handbook-section-result-head'>
                <span className='handbook-section-result-title'>{t}</span>
                <span
                  className={`handbook-setup-state ${hit ? 'neutral' : 'missing'}`}
                >
                  {hit ? `From ${fillSourceTitle(hit)}` : 'Blank'}
                </span>
              </div>
              {hit && (
                <p className='handbook-section-result-body'>
                  {preview(hit.bodyMarkdown)}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {blank.length > 0 && (
        <section className='handbook-check warning'>
          <div className='handbook-check-row unclear'>
            <AlertTriangleIcon aria-hidden='true' />
            <div>
              <p className='handbook-check-row-text'>
                {blank.length === 1
                  ? 'You’ve left this page blank, so your secretary will say it doesn’t know the answer:'
                  : 'You’ve left these pages blank, so your secretary will say it doesn’t know the answer:'}
              </p>
              <ul className='handbook-check-issues'>
                {blank.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
              <p className='handbook-check-row-text handbook-check-next'>
                You can fill these in later from your Handbook, before or after
                you publish.
              </p>
            </div>
          </div>
        </section>
      )}
      <p className='handbook-setup-lead'>
        Your pages are added as drafts. Nothing goes live until you publish.
      </p>
    </div>
  );

  const heading = existing
    ? step === 'pages'
      ? `Create pages from a document`
      : `Your pages in ${existing.title}`
    : step === 'about'
      ? 'Add a section'
      : step === 'pages'
        ? 'Add pages to your section'
        : 'Your section';
  const nextLabel =
    step === 'result'
      ? busy
        ? existing
          ? 'Saving…'
          : 'Adding…'
        : existing
          ? 'Save pages'
          : 'Add section'
      : filling
        ? 'Filling your pages…'
        : 'Next';
  const nextDisabled =
    working ||
    (step === 'about' && (!title.trim() || !!sectionConflict)) ||
    (step === 'pages' &&
      (titles.length === 0 ||
        hasConflict ||
        !!uploadFieldProblem(files) ||
        (!!existing && files.length === 0)));

  return (
    <OrganisationModal
      icon={<ProjectsFolderIcon />}
      title={heading}
      error={error}
      busy={working}
      testId='handbook-section-wizard'
      className='handbook-setup-modal'
      onClose={onClose}
      onSubmit={submit}
      actions={
        <>
          <button
            type='button'
            className='organisation-modal-cancel'
            disabled={working}
            onClick={at === 0 ? onClose : () => setStep(steps[at - 1])}
          >
            {at === 0 ? 'Cancel' : 'Back'}
          </button>
          <button
            type='submit'
            className='organisation-modal-primary organisation-barrier-submit'
            disabled={nextDisabled}
            aria-busy={working}
          >
            {working && (
              <span
                className='organisation-barrier-spinner'
                aria-hidden='true'
              />
            )}
            {nextLabel}
          </button>
        </>
      }
    >
      <div className='handbook-setup-progress'>
        <span className='handbook-setup-progress-text'>
          Step {at + 1} of {steps.length}
        </span>
        <div className='handbook-setup-progress-bar' aria-hidden='true'>
          {steps.map((s, i) => (
            <span key={s} className={i <= at ? 'done' : ''} />
          ))}
        </div>
      </div>
      <div className='handbook-setup-body'>
        {step === 'about' && renderAbout()}
        {step === 'pages' && renderPages()}
        {step === 'result' && renderResult()}
      </div>
    </OrganisationModal>
  );
};
